/**
 * Shared Chart.js theming.
 *
 * Registers Chart.js components once for the whole app and provides option
 * builders so every chart picks up identical dark-theme styling. Pages should
 * call these rather than hand-writing `options` objects.
 */
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { neutral, surface, border, categoryPalette, fontStack } from './tokens';

// Register once at module load. Importing this module anywhere is enough.
ChartJS.register(
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Global defaults so any chart that slips through still looks right.
ChartJS.defaults.color = neutral[400];
ChartJS.defaults.font.family = fontStack;
ChartJS.defaults.font.size = 12;
ChartJS.defaults.borderColor = border.standard;

const GRID = 'rgba(200, 216, 255, 0.09)';
const TICK = neutral[400];

/** Tooltip styling shared by every chart. */
export const tooltipStyle = {
  backgroundColor: surface.overlay,
  titleColor: '#EEF4FF',
  bodyColor: '#D1D5DB',
  borderColor: border.strong,
  borderWidth: 1,
  padding: 12,
  cornerRadius: 8,
  boxPadding: 6,
  usePointStyle: true,
  titleFont: { family: fontStack, weight: '600', size: 12 },
  bodyFont: { family: fontStack, size: 12 },
};

/** Legend styling shared by every chart. */
export const legendStyle = (position = 'top') => ({
  position,
  labels: {
    color: neutral[300],
    padding: 16,
    boxWidth: 8,
    boxHeight: 8,
    usePointStyle: true,
    pointStyle: 'circle',
    font: { family: fontStack, size: 12 },
  },
});

const cartesianScales = ({ yTitle, stacked = false } = {}) => ({
  x: {
    stacked,
    grid: { display: false, drawBorder: false },
    border: { display: false },
    ticks: { color: TICK, maxRotation: 0, autoSkipPadding: 12 },
  },
  y: {
    stacked,
    beginAtZero: true,
    grid: { color: GRID, drawBorder: false },
    border: { display: false },
    ticks: { color: TICK, padding: 8 },
    ...(yTitle
      ? {
          title: {
            display: true,
            text: yTitle,
            color: TICK,
            font: { family: fontStack, size: 12, weight: '500' },
          },
        }
      : {}),
  },
});

/** Options for bar charts. */
export const barOptions = ({ yTitle, stacked = false, legend = false, horizontal = false } = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: horizontal ? 'y' : 'x',
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: legend ? legendStyle('top') : { display: false },
    tooltip: tooltipStyle,
  },
  scales: cartesianScales({ yTitle, stacked }),
});

/** Options for line/area charts. */
export const lineOptions = ({ yTitle, legend = true, stacked = false } = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: legend ? legendStyle('top') : { display: false },
    tooltip: tooltipStyle,
  },
  scales: cartesianScales({ yTitle, stacked }),
  elements: {
    line: { tension: 0.35, borderWidth: 2 },
    point: { radius: 0, hoverRadius: 5, hitRadius: 12 },
  },
});

/** Options for doughnut/pie charts. */
export const doughnutOptions = ({ legendPosition = 'right', cutout = '68%' } = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  cutout,
  plugins: {
    legend: legendStyle(legendPosition),
    tooltip: tooltipStyle,
  },
});

/** Options for polar-area charts. */
export const polarOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: legendStyle('right'), tooltip: tooltipStyle },
  scales: {
    r: {
      grid: { color: GRID },
      angleLines: { color: GRID },
      ticks: { color: TICK, backdropColor: 'transparent' },
    },
  },
});

/** Border/background styling applied to a category-coloured dataset. */
export const categoryDatasetStyle = (categories) => ({
  backgroundColor: categories.map(
    (c) => (categoryPalette[c] || categoryPalette.Other).main
  ),
  borderColor: categories.map(
    (c) => (categoryPalette[c] || categoryPalette.Other).main
  ),
  borderWidth: 0,
  borderRadius: 6,
  hoverOffset: 6,
});
